<template>
  <v-container>
    <v-alert dismissible :type="alertType" v-model="showAlert">
      {{ alertMessage }}
    </v-alert>
    <v-card class="check-in-sheet__card rounded-xl" max-width="900px">
      <v-card-title class="ma-2">
        {{ company_name }}
        <v-tooltip v-if="isAdmin" right>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              v-bind="attrs"
              v-on="on"
              class="pa-4 ml-2"
              small
              :color="frozenPrice"
            >
              mdi-checkbox-blank-circle
            </v-icon>
          </template>
          <span>{{ liveDesc }}</span>
        </v-tooltip>
      </v-card-title>
      <v-data-table
        :loading="isFetching"
        loading-text="Loading..."
        class="check-in-sheet__table"
        hide-default-footer
        :items-per-page="-1"
        :search="searchTable"
        height="400px"
        fixed-header
        :headers="displayHeaders"
        :items="convertersWithSubtotal"
      >
        <!-- SEARCH BAR -->
        <template v-slot:top>
          <v-row class="check-in-sheet__table__search">
            <v-col :cols="searchColumn">
              <v-text-field
                v-model="searchTable"
                label="Search"
                class="mx-4"
              ></v-text-field>
            </v-col>
            <v-col v-if="isAdmin" :cols="addColumn" class="d-flex ma-0 pa-0 align-center">
              <v-btn
                :disabled="converterNotAdded"
                @click="openConverterDialog"
                color="blue darken-1"
                text
              >
                Add
              </v-btn>
            </v-col>
          </v-row>
        </template>
        <!-- SEARCH BAR END -->
        <!-- Row -->
        <template v-if="isAdmin" v-slot:item="{ item, index }">
          <tr>
            <td class="item--code">
              <v-text-field
                type="text"
                :rules="[rules.notEmpty]"
                v-model="editedItem.code"
                v-if="item.id === editingId"
              />
              <span v-else> {{ item.code }} </span>
            </td>
            <td class="item--cost">
              <v-text-field
                prefix="$"
                type="number"
                step="0.1"
                v-model="editedItem.cost"
                v-if="item.id === editingId"
              />
              <span class="font-weight-black" v-else> ${{ item.cost }} </span>
            </td>
            <td class="item--quantity">
              <v-text-field
                type="number"
                :step="1"
                :min="0"
                v-model="editedItem.quantity"
                v-if="item.id === editingId"
              />
              <div v-else class="d-flex flex-row align-center">
                <v-btn
                  v-if="isAdmin"
                  @click="changeItemQuantity(item, 'increase')"
                  class="green--text"
                  text
                  rounded
                  icon
                >
                  <v-icon style="font-size: 12px">mdi-plus</v-icon>
                </v-btn>
                <span> {{ item.quantity }} </span>
                <v-btn
                  v-if="isAdmin"
                  @click="changeItemQuantity(item, 'decrease')"
                  class="red--text"
                  text
                  rounded
                  icon
                >
                  <v-icon style="font-size: 12px">mdi-minus</v-icon>
                </v-btn>
              </div>
            </td>
            <td class="item--subtotal">
              <span class="font-weight-black" v-if="item.id === editingId">
                ${{ (editedItem.cost * editedItem.quantity).toFixed(2) }}</span
              >
              <span v-else class="font-weight-black">{{ item.subtotal }}</span>
            </td>
            <td v-if="isAdmin" class="item--actions">
              <div v-if="item.id != editingId">
                <v-btn text class="green--text" @click="editItem(item, index)">
                  edit
                </v-btn>
                <v-btn
                  text
                  class="red--text"
                  @click="removeConverter(item.code)"
                >
                  delete
                </v-btn>
              </div>
              <div v-else>
                <v-btn text class="green--text" @click="update()">save</v-btn>
                <v-btn text class="red--text" @click="cancel()">cancel</v-btn>
              </div>
            </td>
          </tr>
        </template>
      </v-data-table>
      <!-- ROW - END -->
      <!-- FOOTER -->
      <v-card-actions v-if="isAdmin" class="check-in-sheet__footer">
        <v-dialog v-model="addConverterDialog" max-width="500px">
          <add-catalytic-converter-card
            v-if="addConverterDialog"
            :converters="converters"
            :id="id"
            :user-id="user_id"
            :init-search="searchTable"
            @cancel="cancel"
            @alert-error="displayAlert"
          />
        </v-dialog>
        <v-switch style="font-size:12px" @change="markSheetAsComplete" label="Completed" v-model="completed" />
        <v-spacer />
        <v-btn
          class="red white--text px-3"
          :class="buttonWidth"
          @click="$router.push({ name: 'check-in-sheets' })"
        >
          Cancel
        </v-btn>
        <v-btn
          @click="calculateTotalsAndRoute"
          class="green white--text px-3"
          :class="buttonWidth"
        >
          Next
        </v-btn>
      </v-card-actions>
      <v-card-actions v-else class="check-in-sheet__footer">
        <v-spacer />
        <v-btn
          @click="calculateTotalsAndRoute"
          class="green white--text px-3"
          :class="buttonWidth"
        >
          Totals
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-container>
</template>

<script>
import { mapState } from "@/store/modules/preciousMetals";
import {
  mapActions as checkInSheetMapActions,
  mapState as checkInSheetMapState,
} from "@/store/modules/checkInSheet";
import {mapGetters as userMapGetters } from "@/store/modules/user";
import { extractErrorMessageAxios } from "@/httpClient";
import AddCatalyticConverterCard from "@/components/checkInSheets/AddCatalyticConverterCard";
import { CHECK_IN_SHEET_HEADERS, ITEM } from "@/constants";
import Vue from "vue";

export default {
  created() {
    this.fetchCheckInSheet(this.id);
  },
  props: {
    id: [String, Number],
  },
  components: {
    AddCatalyticConverterCard,
  },
  data() {
    return {
      addConverterDialog: false,
      converters: [],
      searchTable: "",
      fixed_metals_prices: false,
      company_name: "",
      user_id: 0,
      total_items: "",
      avg_converter_price: "",
      editingIndex: -1,
      editingId: -1, 
      subtotal: "",
      hst: 0,
      headers: CHECK_IN_SHEET_HEADERS,
      editedItem: Object.assign({}, ITEM),
      itemBeforeEdit: Object.assign({}, ITEM),
      alertType: "success",
      showAlert: false,
      alertMessage: "",
      exceptionMessage: "",
      rules: {
        notEmpty: (v) => v.length > 0,
      },
      completed: false
    };
  },
  watch: {
    currentCheckInSheet(checkInSheet) {
      this.converters = checkInSheet.catalytic_converters;
      this.fixed_metals_prices = checkInSheet.fixed_metals_prices;
      this.company_name = checkInSheet.company_name;
      this.total_items = checkInSheet.total_items;
      this.subtotal = checkInSheet.subtotal;
      this.hst = checkInSheet.hst;
      this.user_id = checkInSheet.user_id;
      this.completed = checkInSheet.is_complete;
      this.setConverterList(this.converters);
    },
  },
  methods: {
    openConverterDialog() {
      this.addConverterDialog = true;
    },
    async update() {
      try {
        if (this.editedItem.code === "") {
          this.displayAlert("error", "Cannot save an empty code, Please enter a code!");
          return;
        }
        if (this.duplicateCodeInSheet()) {
          this.displayAlert("error", "Duplicate converter code in sheet!");
          return;
        }
        const response = await window.axios.put(
          `/api/check-in-sheet/${this.id}/edit-converter`,
          this.editedItem
        );
        this.displayAlert("success", "Updated Sheet!");
        Vue.set(this.converters, this.editingIndex, this.editedItem);
        this.editingId = -1;
        this.editedItem = Object.assign({}, ITEM);
        this.editingIndex = -1;
      } catch (err) {
        console.error(err);
        this.displayAlert("error", extractErrorMessageAxios(err));
      }
    },
    duplicateCodeInSheet() {
      // if code already exists in the sheet
      for(const [index, converter] of this.converters.entries()) {
        if (index !== this.editingIndex && converter.code === this.editedItem.code) {
          return true;
        }
      }
      return false;
    },
    cancel() {
      this.addConverterDialog = false;
      this.exceptionMessage = null;
      this.searchTable = "";
      this.$nextTick(() => {
        // use editing index to reset the value of the row.
        Vue.set(this.converters, this.editingIndex, this.itemBeforeEdit);
        this.editedItem = Object.assign({}, ITEM);
        this.editingIndex = -1;
        this.editingId = -1;
      });
    },
    removeConverter(code) {
      if (window.confirm("Are you sure you want to delete ?")) {
        const converterIndex = this.converters.findIndex(
          (converter) => converter.code === code
        );
        this.converters.splice(converterIndex, 1);
        try {
          window.axios.post(`/api/check-in-sheet/${this.id}/remove-converter`, {
            code,
          });
        } catch (e) {
          this.displayAlert("error", extractErrorMessageAxios(err));
        }
      }
    },
     editItem(item, index) {
      this.editingId = this.converters.find((converter) => converter.id === item.id)['id'];
      this.editingIndex = this.converters.findIndex((converter => converter.id == item.id));
      this.itemBeforeEdit = JSON.parse(JSON.stringify(this.converters[this.editingIndex]));
      this.editedItem = Object.assign({}, this.converters[this.editingIndex]);
    },
    async changeItemQuantity(item, type) {
      try {
        let indexToEdit = this.converters.findIndex(converter => converter.id === item.id);
        if (type === "increase") {
          item.quantity = parseInt(item.quantity) + 1;
          Vue.set(this.converters, indexToEdit, item);
        } else if (type === "decrease") {
          if (item.quantity > 1) {
            item.quantity -= 1;
            Vue.set(this.converters, indexToEdit, item);
          } else return;
        }
        const response = await window.axios.put(
          `/api/check-in-sheet/${this.id}/edit-converter`,
          item
        );
      } catch (e) {
        this.displayAlert("error", extractErrorMessageAxios(err));
      }
    },
    async calculateTotalsAndRoute() {
      if(this.isAdmin) {
        await this.calculateTotals(this.id);
      }
      this.$router.push(`/check-in-sheets/${this.id}/confirm`);
    },
    displayAlert(type, message) {
      this.alertType = type;
      this.alertMessage = message;
      this.showAlert = true;
    },
    ...checkInSheetMapActions([
      "setConverterList",
      "fetchCheckInSheet",
      "calculateTotals",
    ]),
    async markSheetAsComplete() {
      await window.axios.post(`/api/sheet/${this.currentCheckInSheet.id}/toggle-completion`);
    },
  },
  computed: {
    convertersWithSubtotal() {
      return this.converters.map(converter => ({ ...converter, subtotal: '$ ' + (converter.cost * converter.quantity).toFixed(2) }));
    },
    ...mapState({
      livePrice: "data",
    }),
    ...checkInSheetMapState(["currentCheckInSheet", "isFetching"]),
    ...userMapGetters(['isAdmin']),
    frozenPrice() {
      return !!this.fixed_metals_prices ? "red" : "green";
    },
    liveDesc() {
      return !!this.fixed_metals_prices
        ? `Using Locked Prices Pt: $${this.fixed_metals_prices[0]} USD, Rh: $${this.fixed_metals_prices[1]} USD, Pd: $${this.fixed_metals_prices[2]} USD`
        : `Using Live Market Price`;
    },
    converterNotAdded() {
      return (
        !!this.converters.find((item) => this.searchTable === item.code) ||
        this.searchTable === ""
      );
    },
    buttonWidth() {
      switch (this.$vuetify.breakpoint.name) {
        case "xs":
        case "sm":
          return "button__min";
        case "md":
        case "lg":
        case "xl":
          return "button__full";
      }
    },
    displayHeaders() {
      if (!this.isAdmin)  {
        return this.headers.filter(header => header.value !== 'actions');
      }
      return this.headers;
    },
    searchColumn() {
      return this.isAdmin ? 11 : 12;
    },
    addColumn() {
      return this.isAdmin ? 1 : 0;
    }
  },
};
</script>

<style scoped>
.check-in-sheet__button--delete {
  font-style: normal;
}
.check-in-sheet__card {
  margin: auto;
}
.check-in-sheet__code-name-field {
  width: 300px;
}
.item {
  width: 300px;
}
.button__full {
  width: 200px;
}
.button__min {
  width: "auto";
}
.check-in-sheet__table__search {
  width: 100%;
  margin: -12px 0px -12px 0px;
}
.check-in-sheet__footer {
  border-top: 1px var(--v-accent-lighten2) solid;
}
</style>
